import { getUserError } from '../../../components/miscUtils';
import { getColumnInfo } from '../../../data/analyticsDataSlice';
import { TpDataTable, TpDataTableColumn, VALTYPES } from '../../../data/interfaces';
import { TpBoundData, TpChartConfigParams } from '../charts/chartDataBindings';
import {
  CONFIGPARAM_VISIBILITYCONDITION_TYPES,
  TpChartManifestAspectDef,
  TpChartManifestConfigParamDef,
} from '../charts/interfaces';
import { TpGlobalChartColorMappingConfig } from '../dashboard/interfaces';
import { getAxisDef, getRange } from './axisDef';
import { Color } from './colors';
import { color, getDiscreteColorSet, getNumericalColorSet } from './colors';
import {
  CONFIGPARAMTYPES,
  createConfigParamBoolean,
  createConfigParamTypeDiscreteColorScheme,
  createConfigParamTypeNumericalColorScheme,
  createConfigParamTypeRange,
} from './configParams';
import { convertColumnDataToDisplayString } from './misc';
import { LEGEND_ITEM_TYPES, TpLegendItem } from './renderLegend';

function getTypeDescriptionFromDataType(dataType: string[]): string {
  const typesStrings = [];
  if (dataType.includes(VALTYPES.CATEGORICAL)) typesStrings.push('categorical');
  if (dataType.includes(VALTYPES.NUMBER) || dataType.includes(VALTYPES.INTEGER)) typesStrings.push('numerical');
  return typesStrings.join(' or ');
}

export function createColorMappingAspect(
  aspectId: string,
  aspectName: string,
  groupId: string,
  quality: number,
  targetDisplayName: string,
  dataTypes: (typeof VALTYPES)[keyof typeof VALTYPES][] = [VALTYPES.CATEGORICAL, VALTYPES.NUMBER],
): TpChartManifestAspectDef {
  let description = '';
  if (targetDisplayName) {
    const typesStr = getTypeDescriptionFromDataType(dataTypes);
    description = `A ${typesStr} property used to color code the ${targetDisplayName}.`;
  }
  return {
    id: aspectId,
    name: aspectName,
    description,
    groupId,
    required: false,
    quality,
    dataType: dataTypes,
  };
}

export function createCategoricalOrderConfigParam(
  aspectId: string,
  aspectName: string,
  groupId: string,
): TpChartManifestConfigParamDef {
  return {
    id: `${aspectId}CategoricalColorMapping`,
    name: 'Preset order (for categorical properties)',
    groupId,
    description: `
Optionally specify preset order for categorical values and thus a custom color mapping.
Should be a list of objects with keys propertyId and order, where propertyId is a property aspect ID and order is a list of categorical values in the desired order.

 `,
    hidden: true,
    paramType: { configParamTypeId: CONFIGPARAMTYPES.JSON },
  };
}

export function createColorMappingConfigs(
  aspectId: string,
  aspectName: string,
  groupId: string,
  dataTypes: (typeof VALTYPES)[keyof typeof VALTYPES][] = [VALTYPES.CATEGORICAL, VALTYPES.NUMBER],
): TpChartManifestConfigParamDef[] {
  const configOptions = [];

  if (dataTypes.includes(VALTYPES.CATEGORICAL)) {
    configOptions.push(
      {
        id: `${aspectId}SchemeCategorical`,
        name: `${aspectName} scheme (categorical)`,
        groupId,
        description: `Descrete color palette used to visualize the \`${aspectId}\` chart aspect, in case it contains categorical data.`,
        paramType: createConfigParamTypeDiscreteColorScheme(),
        visibilityConditions: [
          {
            conditionType: CONFIGPARAM_VISIBILITYCONDITION_TYPES.FACET_BOUND,
            conditionInfo: {
              facet: aspectId,
              dataType: VALTYPES.CATEGORICAL,
            },
          },
        ],
      },
      createCategoricalOrderConfigParam(aspectId, aspectName, groupId),
    );
  }
  if (dataTypes.includes(VALTYPES.NUMBER) || dataTypes.includes(VALTYPES.INTEGER)) {
    configOptions.push(
      {
        id: `${aspectId}SchemeNumerical`,
        name: `${aspectName} scheme (numerical)`,
        groupId,
        description: `Continuous color palette used to visualize the \`${aspectId}\` chart aspect, in case it contains numerical data.`,
        paramType: createConfigParamTypeNumericalColorScheme(),
        visibilityConditions: [
          {
            conditionType: CONFIGPARAM_VISIBILITYCONDITION_TYPES.FACET_BOUND,
            conditionInfo: {
              facet: aspectId,
              dataType: VALTYPES.NUMBER,
            },
          },
        ],
      },
      {
        id: `${aspectId}GammaNumerical`,
        name: `${aspectName} gamma factor`,
        description: `Defines a nonlinear conversion of the numerical values used for the \`${aspectId}\` chart aspect.
  This can be used to provide improved focus on the low end or high end range of the values in case of a skewed distribution.`,
        groupId,
        paramType: createConfigParamTypeRange(1, 10, 0.25, 5),
        visibilityConditions: [
          {
            conditionType: CONFIGPARAM_VISIBILITYCONDITION_TYPES.FACET_BOUND,
            conditionInfo: {
              facet: aspectId,
              dataType: VALTYPES.NUMBER,
            },
          },
        ],
      },
      {
        id: `${aspectId}UseDataValsForLegend`,
        name: 'Use data values in color legend',
        groupId,
        description: `If set, the actual numerical data values present in the \`${aspectId}\` chart aspect are used for the chart color legend. This makes most sense if only a few discrete numerical values are present.

  If not, values at rounded intervals are displayed in the legend. This makes most sense if many different values are present`,
        paramType: createConfigParamBoolean(false),
        visibilityConditions: [
          {
            conditionType: CONFIGPARAM_VISIBILITYCONDITION_TYPES.FACET_BOUND,
            conditionInfo: {
              facet: aspectId,
              dataType: VALTYPES.NUMBER,
            },
          },
        ],
      },
      {
        id: `${aspectId}PresetColorsNumerical`,
        name: 'Preset colors (for numerical properties)',
        groupId,
        description: `
        Optionally specify preset colors for preset numerical ranges.
        Should be a list of objects with keys minVal, maxVal, color (r,g,b values) and label.
        `,
        hidden: true,
        paramType: { configParamTypeId: CONFIGPARAMTYPES.JSON },
      },
    );
  }

  return configOptions;
}

export interface TpPresetColorNumerical {
  minVal: number;
  maxVal: number;
  color: { r: number; g: number; b: number };
  label: string;
}

export interface TpColorDataMapperInfo {
  hasColors: boolean;
  hasCategoricalColors: boolean;
  hasNumericalColors: boolean;
  dataColor: any[];
  categoriesCount: number | null;
  colorColumnInfo: TpDataTableColumn | null; // Note: colorColumnInfo.augmentedData.categories actually contains numbers instead of strings.
  colorCategories: string[]; // Fixme: this might currently contain numbers, but we typed it to be the same as colorCat2Index
  colorCat2Index: { [cat: string]: number }; // Fixme: this currently is a number to number map, typescript doesn't pick up because of the use of any type
  colors: Color[];
  colorValueStrings: string[];
  minValColor: number | null;
  maxValColor: number | null;
  gammaFactor: number;
  grayColorStr: string;
  grayColor: Color;
  opacity: number;
  numericalLegendItems: { posit: number; label: string }[] | null;
  presetColorsNumerical: TpPresetColorNumerical[] | null;
}

export function createNumericalScaleStops(
  minVal: number,
  maxVal: number,
  presetColorsNumerical?: TpPresetColorNumerical[],
): { posit: number; label: string }[] {
  // Create numerical color range legend items using an axis scale logic: equidistant intervals
  const numericalLegendItems: { posit: number; label: string }[] = [];
  for (const axisItem of getAxisDef(minVal, maxVal, 7)) {
    // Do not include values that are inside a preset color range.
    const preset = presetColorsNumerical ? getPresetColorRange(presetColorsNumerical, axisItem.posit) : null;
    if (!preset && axisItem.label)
      numericalLegendItems.push({
        posit: axisItem.posit,
        label: axisItem.label,
      });
  }
  return numericalLegendItems;
}

export function createNumericalScaleDataValues(
  colInfo: TpDataTableColumn,
  data: number[],
  presetColorsNumerical?: TpPresetColorNumerical[],
): { posit: number; label: string }[] {
  // Create numerical color range legend items from the actual data values (best option if there are only a few different values in the data set)
  const numericalLegendItems: { posit: number; label: string }[] = [];
  const sortedData = data.filter((val) => !isNaN(val) && val !== null).toSorted((val1, val2) => val1 - val2);
  let lastValue = -1e99;
  for (let i = 0; i < sortedData.length && numericalLegendItems.length < 100; i++) {
    // Do not include values that are inside a preset color range.
    const preset = presetColorsNumerical ? getPresetColorRange(presetColorsNumerical, sortedData[i]) : null;
    if (!preset && sortedData[i] > lastValue) {
      numericalLegendItems.push({
        posit: sortedData[i],
        label: convertColumnDataToDisplayString(colInfo, sortedData[i]),
      });
      lastValue = sortedData[i];
    }
  }
  return numericalLegendItems;
}

export function getCategoricalCustomGlobalConfigOrder(
  globalColorMappingConfig: TpGlobalChartColorMappingConfig | undefined,
  boundData: TpBoundData,
  colorColumnInfo: TpDataTableColumn,
): string[] | null {
  const globalCategoricalOrders = globalColorMappingConfig?.customCategoricalOrders ?? [];
  const applicableGlobalOrder = globalCategoricalOrders.find(
    (order) => order.canonicalTypeId === boundData.canonicalTypeInfo.id && order.propertyId === colorColumnInfo.id,
  );
  return applicableGlobalOrder ? applicableGlobalOrder.order : null;
}

export function getCategoricalCustomConfigOrder(
  config: TpChartConfigParams,
  aspectId: string,
  colorColumnInfo: TpDataTableColumn,
): string[] | null {
  const presetOrderKey = `${aspectId}CategoricalColorMapping`;
  if (!config[presetOrderKey]) return null;

  const presetOrders: { propertyId: string; order: string[] }[] = config[presetOrderKey];
  if (!Array.isArray(presetOrders)) return null;
  const presetOrderForAspect = presetOrders.find((preset) => preset.propertyId === colorColumnInfo.id);
  return presetOrderForAspect ? presetOrderForAspect.order : null;
}

/**
 * Get the order of categorical values for a color aspect, based on a custom order if provided.
 * @param colorColumnInfo - The column info of the color aspect, which contains the categorical values and their order in the data.
 * @param customOrder - An optional custom order for the categorical values provided as a list of category names, not values!
 * @returns An array of categorical values in the order they should be displayed and colored in the chart.
 */
function getCategoricalValueOrder(colorColumnInfo: TpDataTableColumn, customOrder: string[] | null): any[] {
  const baseOrder = colorColumnInfo.augmentedData.categories!.map((cat) => cat.value);
  if (!customOrder) return baseOrder;

  // Convert the category names to values
  const presetOrderValues = customOrder
    .map((catName) => {
      return colorColumnInfo.catConvertorInv?.[catName] ?? null;
    })
    .filter((catValue) => catValue !== null);

  // This should not be needed after we fix the interface type
  const baseOrderNumbers = baseOrder.map((catValue) => (typeof catValue === 'number' ? catValue : parseInt(catValue)));

  // Order the preset categories according to the preset order, add all categories from the data
  // and then remove duplicates while preserving the order (in case the preset order doesn't contain all categories from the data)
  return [...new Set([...presetOrderValues, ...baseOrderNumbers])];
}

export function createColorMappingInfo(
  config: TpChartConfigParams,
  boundData: TpBoundData,
  colorAspectId: string,
  opacity: number,
  globalColorMappingConfig?: TpGlobalChartColorMappingConfig,
): TpColorDataMapperInfo {
  const dataTable: TpDataTable = boundData.dataTable;

  const dataColor = boundData.bindings[colorAspectId] && boundData.bindings[colorAspectId].data;

  let colorColumnInfo: TpDataTableColumn | null = null;
  if (dataColor) colorColumnInfo = getColumnInfo(dataTable, boundData.bindings[colorAspectId].columnId);

  const hasCategoricalColors =
    dataColor && getColumnInfo(dataTable, boundData.bindings[colorAspectId].columnId).valueType == VALTYPES.CATEGORICAL;
  const hasNumericalColors =
    dataColor &&
    [VALTYPES.NUMBER, VALTYPES.INTEGER].includes(
      getColumnInfo(dataTable, boundData.bindings[colorAspectId].columnId).valueType,
    );

  let categoriesCount: any = null;
  let colorCategories: string[] = [];
  let colorCat2Index = null;
  let colors: Color[] = [];
  if (hasCategoricalColors) {
    categoriesCount = colorColumnInfo!.augmentedData.categories!.length;
    const customGlobalConfigOrder = getCategoricalCustomGlobalConfigOrder(
      globalColorMappingConfig,
      boundData,
      colorColumnInfo!,
    );
    const customConfigOrder = getCategoricalCustomConfigOrder(config, colorAspectId, colorColumnInfo!);
    colorCategories = getCategoricalValueOrder(colorColumnInfo!, customConfigOrder || customGlobalConfigOrder);
    colorCat2Index = colorCategories.reduce((acc: any, catValue, idx) => {
      acc[catValue] = idx;
      return acc;
    }, {});
    const colorSchemeCategorical = config[`${colorAspectId}SchemeCategorical`];
    colors = getDiscreteColorSet(colorSchemeCategorical).colors;
  }

  let minValColor: number | null = null;
  let maxValColor: number | null = null;
  let numericalLegendItems: { posit: number; label: string }[] | null = null;
  let gammaFactor = 1;
  if (hasNumericalColors) {
    const expandZeroVariance = false; // If there is only 1 value, keep it.
    [minValColor, maxValColor] = getRange(dataColor, 0.0, expandZeroVariance);
    const hasZeroVariance = minValColor === maxValColor;

    const colorSchemeNumerical = config[`${colorAspectId}SchemeNumerical`];
    colors = getNumericalColorSet(colorSchemeNumerical).colors;
    const gammaPickValue = config[`${colorAspectId}GammaNumerical`] / 10;
    gammaFactor = gammaPickValue ** 3 / 0.5 ** 3;
    numericalLegendItems = [];
    const presetNumericalColors: TpPresetColorNumerical[] | undefined = config[`${colorAspectId}PresetColorsNumerical`];
    if (config[`${colorAspectId}UseDataValsForLegend`] || hasZeroVariance) {
      numericalLegendItems = createNumericalScaleDataValues(colorColumnInfo!, dataColor, presetNumericalColors);
    } else {
      numericalLegendItems = createNumericalScaleStops(minValColor, maxValColor, presetNumericalColors);
    }
  }

  const colorValueStrings = colors.map((color) => color.opacity(opacity).toCanvasString());

  const grayColor = color(128, 128, 128);
  const grayColorStr = grayColor.opacity(opacity).toCanvasString();

  return {
    hasColors: hasCategoricalColors || hasNumericalColors,
    hasCategoricalColors,
    hasNumericalColors,
    dataColor,
    colorColumnInfo,
    categoriesCount,
    colorCategories,
    colorCat2Index,
    colors,
    colorValueStrings,
    minValColor,
    maxValColor,
    gammaFactor,
    grayColorStr,
    grayColor,
    opacity,
    numericalLegendItems,
    presetColorsNumerical: config[`${colorAspectId}PresetColorsNumerical`] || null,
  };
}

function getPresetColorRange(
  presetColorsNumerical: TpPresetColorNumerical[],
  value: number,
): TpPresetColorNumerical | null {
  for (const preset of presetColorsNumerical) {
    if (value >= preset.minVal && value <= preset.maxVal) {
      return preset;
    }
  }
  return null;
}

function getNumericalPresetColor(colorMappingInfo: TpColorDataMapperInfo, value: number): Color | null {
  if (!colorMappingInfo.presetColorsNumerical) return null;
  const preset = getPresetColorRange(colorMappingInfo.presetColorsNumerical, value);
  if (preset) {
    return color(preset.color.r, preset.color.g, preset.color.b, colorMappingInfo.opacity);
  }
  return null;
}

export function getColorFrac(value: number, minValColor: number, maxValColor: number, gammaFactor: number): number {
  if (isNaN(value)) return 0;
  if (minValColor === maxValColor) return 0.5;
  const colorFrac = Math.max(1e-99, Math.min(1, (value - minValColor) / (maxValColor - minValColor))) ** gammaFactor;
  return colorFrac;
}

export function getColorStringIdx(colorValueStrings: string[], colorFrac: number): number {
  return Math.round(Math.max(0, Math.min(colorValueStrings.length - 1, colorFrac * colorValueStrings.length)));
}

export function getMappedColorStringDataPoint(colorMappingInfo: TpColorDataMapperInfo, itemNr: number): string[] {
  // returns fill & stroke colors
  const value = colorMappingInfo.dataColor[itemNr];
  if (colorMappingInfo.hasCategoricalColors) {
    const colorIdx = colorMappingInfo.colorCat2Index[value];
    if (colorIdx < colorMappingInfo.colorValueStrings.length) {
      const color = colorMappingInfo.colorValueStrings[colorIdx];
      return [color, color];
    } else {
      return [colorMappingInfo.grayColorStr, colorMappingInfo.grayColorStr];
    }
  }
  if (colorMappingInfo.hasNumericalColors) {
    if (isNaN(value)) return [colorMappingInfo.grayColorStr, colorMappingInfo.grayColorStr];
    const presetColor = getNumericalPresetColor(colorMappingInfo, value);
    if (presetColor) return [presetColor.toCanvasString(), presetColor.toCanvasString()];
    const colorFrac = getColorFrac(
      value,
      colorMappingInfo.minValColor!,
      colorMappingInfo.maxValColor!,
      colorMappingInfo.gammaFactor,
    );
    const colorIdx = getColorStringIdx(colorMappingInfo.colorValueStrings, colorFrac);
    const colorStr = colorMappingInfo.colorValueStrings[colorIdx];
    return [colorStr, colorStr];
  }
  return [colorMappingInfo.grayColorStr, colorMappingInfo.grayColorStr];
}

export function getMappedColorDataPoint(colorMappingInfo: TpColorDataMapperInfo, itemNr: number): Color {
  // returns fill & stroke colors
  const value = colorMappingInfo.dataColor[itemNr];
  if (colorMappingInfo.hasCategoricalColors) {
    const colorIdx = colorMappingInfo.colorCat2Index[value];
    if (colorIdx < colorMappingInfo.colors.length) {
      const color = colorMappingInfo.colors[colorIdx];
      return color;
    } else {
      return colorMappingInfo.grayColor;
    }
  }
  if (colorMappingInfo.hasNumericalColors) {
    if (isNaN(value)) return colorMappingInfo.grayColor;
    const presetColor = getNumericalPresetColor(colorMappingInfo, value);
    if (presetColor) return presetColor;
    const colorFrac = getColorFrac(
      value,
      colorMappingInfo.minValColor!,
      colorMappingInfo.maxValColor!,
      colorMappingInfo.gammaFactor,
    );
    const colorIdx = getColorStringIdx(colorMappingInfo.colorValueStrings, colorFrac);
    const color = colorMappingInfo.colors[colorIdx];
    return color;
  }
  return colorMappingInfo.grayColor;
}

export function getMappedColorStringCategorical(colorMappingInfo: TpColorDataMapperInfo, catValue: any): string {
  if (!colorMappingInfo.hasCategoricalColors) {
    throw getUserError('Color mapping is not categorical');
  }
  const colorIdx = colorMappingInfo.colorCat2Index[catValue];
  if (colorIdx < colorMappingInfo.colorValueStrings.length) {
    return colorMappingInfo.colorValueStrings[colorIdx];
  } else {
    return colorMappingInfo.grayColorStr;
  }
}

export function getMappedColorCategorical(colorMappingInfo: TpColorDataMapperInfo, catValue: any): Color {
  if (!colorMappingInfo.hasCategoricalColors) {
    throw getUserError('Color mapping is not categorical');
  }
  const colorIdx = colorMappingInfo.colorCat2Index[catValue];
  if (colorIdx < colorMappingInfo.colors.length) {
    return colorMappingInfo.colors[colorIdx];
  } else {
    return colorMappingInfo.grayColor;
  }
}

export function getMappedColorLegendInfo(colorMappingInfo: TpColorDataMapperInfo): TpLegendItem {
  if (colorMappingInfo.hasCategoricalColors)
    return {
      type: LEGEND_ITEM_TYPES.CategoricalColors,
      colorColumnInfo: colorMappingInfo.colorColumnInfo!,
      colorValueStrings: colorMappingInfo.colorValueStrings!,
      colorCategories: colorMappingInfo.colorCategories,
      colorCat2Index: colorMappingInfo.colorCat2Index,
    };

  if (colorMappingInfo.hasNumericalColors)
    return {
      type: LEGEND_ITEM_TYPES.NumericalColors,
      colorColumnInfo: colorMappingInfo.colorColumnInfo!,
      colorValueStrings: colorMappingInfo.colorValueStrings!,
      minValColor: colorMappingInfo.minValColor!,
      maxValColor: colorMappingInfo.maxValColor!,
      gammaFactor: colorMappingInfo.gammaFactor,
      numericalLegendItems: colorMappingInfo.numericalLegendItems!,
      presetColorsNumerical: colorMappingInfo.presetColorsNumerical,
    };

  throw getUserError('Color mapping error');
}
